import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  applyEdgeChanges,
  applyNodeChanges,
  Node,
  Edge,
  type OnEdgesChange,
  type OnNodesChange,
} from "@xyflow/react";

type State = {
  allNodes: Node[];
  nodes: Node[];
  edges: Edge[];
  nodeToFocusOn?: { id: string; isCollapsed: boolean };
  isDialogOpen: boolean;
  areaDataForDialog: { id: string; name: string };
  searchedNodeId: string | null;
  visibleNodesRef: Map<string, boolean>;
  childrenCollapsedStatus: Map<string, boolean>;
  wasSearchedNodeCleared: boolean;
};

type Action = {
  setAllNodes: (nodes: State["allNodes"]) => void;
  setNodes: (nodes: State["nodes"]) => void;
  setEdges: (edges: State["edges"]) => void;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  setNodeToFocusOn: (nodesToFocusOn: State["nodeToFocusOn"]) => void;
  setIsDialogOpen: (isDialogOpen: State["isDialogOpen"]) => void;
  setAreaDataForDialog: (areaDataForDialog: State["areaDataForDialog"]) => void;
  setSearchedNodeId: (searchedNodeId: State["searchedNodeId"]) => void;
  setVisibleNodesRef: (visibleNodesRef: State["visibleNodesRef"]) => void;
  setChildrenCollapsedStatus: (
    childrenCollapsedStatus: State["childrenCollapsedStatus"]
  ) => void;
  setWasSearchedNodeCleared: (
    wasSearchedNodeCleared: State["wasSearchedNodeCleared"]
  ) => void;
  updateVisibleNodeRef: (nodeId: string, isVisible: boolean) => void;
  toggleChildrenCollapsedStatus: (nodeId: string) => void;
};

const useOrgchartAreasStore = create<State & Action>()(
  devtools((set, getState) => ({
    allNodes: [],
    nodes: [],
    edges: [],
    isDialogOpen: false,
    areaDataForDialog: { id: "", name: "" },
    searchedNodeId: null,
    visibleNodesRef: new Map<string, boolean>(),
    childrenCollapsedStatus: new Map<string, boolean>(),
    wasSearchedNodeCleared: false,
    nodeToFocusOn: undefined,
    setAllNodes: (allNodes) => set({ allNodes }),
    onNodesChange: (changes) =>
      set({ nodes: applyNodeChanges(changes, getState().nodes) }),
    onEdgesChange: (changes) =>
      set({ edges: applyEdgeChanges(changes, getState().edges) }),
    setNodes: (nodes) => set({ nodes }),
    setEdges: (edges) => set(() => ({ edges })),
    setNodeToFocusOn: (nodeToFocusOn) => set({ nodeToFocusOn }),
    setIsDialogOpen: (isDialogOpen) => set({ isDialogOpen }),
    setAreaDataForDialog: (areaDataForDialog) => set({ areaDataForDialog }),
    setSearchedNodeId: (searchedNodeId) => set({ searchedNodeId }),
    setVisibleNodesRef: (visibleNodesRef) => set({ visibleNodesRef }),
    setChildrenCollapsedStatus: (childrenCollapsedStatus) =>
      set({ childrenCollapsedStatus }),
    setWasSearchedNodeCleared: (wasSearchedNodeCleared) =>
      set({ wasSearchedNodeCleared }),
    updateVisibleNodeRef: (nodeId, isVisible) =>
      set((state) => ({
        visibleNodesRef: new Map(state.visibleNodesRef).set(nodeId, isVisible),
      })),
    toggleChildrenCollapsedStatus: (nodeId) =>
      set((state) => {
        const currentStatus = state.childrenCollapsedStatus.get(nodeId);
        return {
          childrenCollapsedStatus: new Map(state.childrenCollapsedStatus).set(
            nodeId,
            !currentStatus
          ),
        };
      }),
  }))
);

export default useOrgchartAreasStore;
